import { Children, memo, MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useLocation, useNavigate } from "react-router";
import { useIsMobileView } from "../../utils";
import { Button, ButtonProps } from "../Button";
import { PageLayout, PageLayoutProps } from "../layout";
import { ProgressStepItem } from "../ProgressSteps/ProgressSteps/types";
import { Scrollbars } from "../Scrollbars";
import { SectionHeader, SectionHeaderPropsType } from "../SectionHeader";
import { TooltipProps } from "../Tooltip";
import { ProgressStepWrapper, ProgressStepWrapperProps } from "./ProgressStep";
import { StepperContext } from "./StepperContext";

import styles from "./style.module.scss";

export type StepperInstance = {
  stepIndex: number,
  setStepIndex: (index: number) => void,
}

export type StepDefinition = ProgressStepItem & {
  onNext?: (setStepIndex: (v: number) => void) => boolean | Promise<boolean>
  onBack?: (setStepIndex: (v: number) => void) => boolean | Promise<boolean>
  children: ReactNode
  stepperRef?: MutableRefObject<StepperInstance | undefined>
  backButtonProps?: ButtonProps & { hidden?: boolean }
  nextButtonProps?: ButtonProps & { hidden?: boolean }
}

export type StepperProps = PageLayoutProps & {
  steps?: StepDefinition[]
  onStepChange?: (index: number, step: StepDefinition) => void
  progressStepProps?: ProgressStepWrapperProps
  sectionHeaderProps?: Omit<SectionHeaderPropsType, "header" | "supportingText">
  tooltipProps?: Omit<TooltipProps, "content" | "supportingContent" | "children">
  useURLSearchParams?: boolean
  showBackButtonOnFirstStep?: boolean
}

export const Stepper = memo<StepperProps>((
  {
    steps = [],
    onStepChange,
    tooltipProps,
    progressStepProps,
    sectionHeaderProps,
    useURLSearchParams = true,
    showBackButtonOnFirstStep,
    ...restProps
  }) => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [ initialized, setInitialized ] = useState(false);
  const [ stepIndex, setStepIndexInternal ] = useState(0);

  const isMobileView = useIsMobileView();

  const setStepIndex = useCallback((index: number) => {
    const step = Math.min(Math.max(index, 0), steps.length - 1);
    if (useURLSearchParams) {
      navigate({
        search: new URLSearchParams({ step: step.toString() }).toString(),
      });
    } else {
      setStepIndexInternal(step);
    }
  }, [ steps.length, navigate, useURLSearchParams ]);

  useEffect(() => {
    if (!initialized) {
      setInitialized(true);
      setStepIndex(0);
    } else if (useURLSearchParams) {
      setStepIndexInternal(Number(new URLSearchParams(search).get("step")) || 0);
    }
  }, [ initialized, setInitialized, setStepIndex, setStepIndexInternal, search, useURLSearchParams ]);

  const currentStep = steps[stepIndex];

  useEffect(() => {
    onStepChange?.(stepIndex, currentStep);
  }, [ onStepChange, stepIndex, currentStep ]);

  steps.forEach(value => {
    if (value?.stepperRef) {
      value.stepperRef.current = {
        stepIndex,
        setStepIndex,
      };
    }
  });

  const onBackClick = useCallback(async () => {
    if (!currentStep?.onBack || await currentStep?.onBack(setStepIndex)) {
      setStepIndex(stepIndex - 1);
    }
  }, [ currentStep, setStepIndex, stepIndex ]);

  const onNextClick = useCallback(async () => {
    if (!currentStep?.onNext || await currentStep?.onNext(setStepIndex)) {
      setStepIndex(stepIndex + 1);
    }
  }, [ currentStep, setStepIndex, stepIndex ]);

  const showStepInfo = isMobileView || (progressStepProps?.typeStepper === "progressMinimalIconsConnected" && tooltipProps?.disabled);
  const typeStepper = isMobileView ? "progressMinimalIconsConnected" : progressStepProps?.typeStepper;

  return (
    <StepperContext.Provider value={useMemo(() => ({
      setNextStep: onNextClick,
      setPreviousStep: onBackClick,
    }), [ onNextClick, onBackClick ])}>
      <PageLayout
        actions={
          <>
            {
              ((stepIndex !== 0 || showBackButtonOnFirstStep) && !currentStep.backButtonProps?.hidden) &&
              <Button
                buttonType="secondaryGray"
                {...currentStep.backButtonProps}
                className={classNames(styles.backButton, currentStep.backButtonProps?.className)}
                onClick={onBackClick}
              >
                {Children.count(currentStep.backButtonProps?.children) > 0 ? currentStep.backButtonProps?.children : "Back"}
              </Button>
            }
            {
              !currentStep.nextButtonProps?.hidden &&
              <Button
                buttonType="primary"
                {...currentStep.nextButtonProps}
                className={classNames(styles.nextButton, currentStep.nextButtonProps?.className)}
                onClick={onNextClick}
              >
                {Children.count(currentStep.nextButtonProps?.children) > 0 ? currentStep.nextButtonProps?.children : "Next"}
              </Button>
            }
          </>
        }
        {...restProps}
        pageLayoutContentClassName={classNames(
          styles.pageLayoutContent,
          restProps.pageLayoutContentClassName,
          isMobileView ? styles.mobileView : styles[progressStepProps?.typeStepper as keyof typeof styles],
        )}
      >
        <ProgressStepWrapper
          className={classNames(styles.steps, progressStepProps?.className)}
          steps={steps}
          currentStep={stepIndex}
          {...progressStepProps}
          typeStepper={typeStepper}
          {...(typeStepper === "progressMinimalIconsConnected" && { tooltipProps })}
        />
        <SectionHeader
          {...sectionHeaderProps}
          className={classNames(styles.sectionHeader, sectionHeaderProps?.className)}
          header={
            showStepInfo
              ? `${currentStep?.text} (Step ${stepIndex + 1}/${steps.length})`
              : `Step ${stepIndex + 1}/${steps.length}`
          }
          supportingText={
            showStepInfo
              ? `${currentStep?.supportingText}`
              : undefined
          }
        />
        <Scrollbars className={classNames(styles.currentStep)}>
          {currentStep?.children}
        </Scrollbars>
      </PageLayout>
    </StepperContext.Provider>
  );
});